import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export interface AdGeneration {
  id: string;
  user_id: string;
  business_profile_id: string | null;
  business_name: string | null;
  business_type: string | null;
  audience: string | null;
  offer: string | null;
  platform: string | null;
  tone: string | null;
  hooks: string[] | null;
  headlines: string[] | null;
  subheadlines: string[] | null;
  primary_text: string[] | null;
  ctas: string[] | null;
  overlay_text: { headline: string; subheadline: string; cta: string } | null;
  image_storage_urls: string[] | null;
  video_storage_urls: string[] | null;
  video_audio_urls: string[] | null;
  video_voiceover_scripts: string[] | null;
  tags: string[] | null;
  created_at: string;
}

function compactUrlArray(urls: unknown): string[] | null {
  if (!Array.isArray(urls)) return null;
  const compacted = urls
    .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .map((url) => url.trim());
  return compacted.length > 0 ? compacted : null;
}

function normalizeAdGeneration(item: AdGeneration): AdGeneration {
  return {
    ...item,
    image_storage_urls: compactUrlArray(item.image_storage_urls),
    video_storage_urls: compactUrlArray(item.video_storage_urls),
    video_audio_urls: compactUrlArray(item.video_audio_urls),
    video_voiceover_scripts: compactUrlArray(item.video_voiceover_scripts),
  };
}

export function useAdHistory(limit = 20) {
  const { user } = useAuth();
  const [history, setHistory] = useState<AdGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track whether a fetch is already in-flight to prevent duplicate calls
  const fetchingRef = useRef(false);

  const fetchHistory = useCallback(async () => {
    if (!user) {
      setHistory([]);
      setLoading(false);
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    const { data, error: err } = await supabase
      .from('ad_generations')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (err) {
      setError(err.message);
    } else {
      setHistory((data ?? []).map((item) => normalizeAdGeneration(item as AdGeneration)));
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [user, limit]);

  // Initial fetch + refetch whenever user/limit changes
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // ── Realtime subscription ─────────────────────────────────────────────────
  // Listen for INSERT/UPDATE/DELETE events on ad_generations for this user.
  // This ensures history auto-updates right after a new ad is generated —
  // even if the history page was already open.
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`ad_generations_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ad_generations',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Re-fetch the full list whenever anything changes
          fetchHistory();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchHistory]);

  // ── Window focus refetch ──────────────────────────────────────────────────
  // When the user switches back to this tab, silently refresh the list.
  useEffect(() => {
    const handleFocus = () => {
      if (user) fetchHistory();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && user) fetchHistory();
    });
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, fetchHistory]);

  const deleteGeneration = useCallback(async (id: string): Promise<void> => {
    setHistory((prev) => prev.filter((item) => item.id !== id));
    const { error: delErr } = await supabase
      .from('ad_generations')
      .delete()
      .eq('id', id);
    if (delErr) {
      setError(delErr.message);
      fetchHistory();
    }
  }, [fetchHistory]);

  const bulkDeleteGenerations = useCallback(async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    setHistory((prev) => prev.filter((item) => !ids.includes(item.id)));
    const { error: delErr } = await supabase
      .from('ad_generations')
      .delete()
      .in('id', ids);
    if (delErr) {
      setError(delErr.message);
      fetchHistory();
    }
  }, [fetchHistory]);

  const updateTags = useCallback(async (id: string, tags: string[]): Promise<void> => {
    setHistory((prev) =>
      prev.map((item) => item.id === id ? { ...item, tags } : item)
    );
    const { error: updateErr } = await supabase
      .from('ad_generations')
      .update({ tags })
      .eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
      fetchHistory();
    }
  }, [fetchHistory]);

  return { history, loading, error, refetch: fetchHistory, deleteGeneration, bulkDeleteGenerations, updateTags };
}
